import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/toast';
import { useCreateSupportTicket } from '@/features/support/api';
import { closeFeedbackModal, useFeedbackModal } from './feedback-store';
import type { Severity } from '@/features/support/types';

const SUBJECT_LIMIT = 200;
const DESCRIPTION_LIMIT = 4000;

/**
 * Modal for the FeedbackButton + the auto-flow on a 5xx. Captures subject /
 * description / severity, hard-includes the current URL + user-agent for
 * triage, and forwards any pre-filled `sentryEventId` so the ticket links
 * back to the captured error.
 *
 * Mounted once in the AppShell next to FeedbackButton.
 */
export function FeedbackModal(): JSX.Element {
  const { t } = useTranslation();
  const { open, prefill } = useFeedbackModal();

  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<Severity>('medium');

  const createMutation = useCreateSupportTicket();

  useEffect(() => {
    if (open) {
      setSubject(prefill.subject ?? '');
      setDescription(prefill.description ?? '');
      setSeverity(prefill.severity ?? 'medium');
    }
  }, [open, prefill]);

  function handleClose(): void {
    closeFeedbackModal();
  }

  async function handleSubmit(): Promise<void> {
    if (!subject.trim() || !description.trim()) return;
    try {
      await createMutation.mutateAsync({
        subject: subject.trim(),
        description: description.trim(),
        severity,
        url: typeof window !== 'undefined' ? window.location.href : undefined,
        userAgent:
          typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        sentryEventId: prefill.sentryEventId ?? null,
      });
      toast.success(t('feedback.modal.successTitle'), {
        description: t('feedback.modal.successDescription'),
      });
      handleClose();
    } catch (err) {
      toast.error(t('feedback.modal.errorTitle'), {
        description: err instanceof Error ? err.message : String(err),
      });
    }
  }

  const submitting = createMutation.isPending;
  const valid = subject.trim().length > 0 && description.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? null : handleClose())}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('feedback.modal.title')}</DialogTitle>
          <DialogDescription>{t('feedback.modal.description')}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium" htmlFor="feedback-subject">
              {t('feedback.modal.fields.subject')}
            </label>
            <Input
              id="feedback-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value.slice(0, SUBJECT_LIMIT))}
              placeholder={t('feedback.modal.fields.subjectPlaceholder')}
              maxLength={SUBJECT_LIMIT}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium" htmlFor="feedback-description">
              {t('feedback.modal.fields.description')}
            </label>
            <Textarea
              id="feedback-description"
              value={description}
              onChange={(e) =>
                setDescription(e.target.value.slice(0, DESCRIPTION_LIMIT))
              }
              placeholder={t('feedback.modal.fields.descriptionPlaceholder')}
              rows={6}
              maxLength={DESCRIPTION_LIMIT}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium">
              {t('feedback.modal.fields.severity')}
            </label>
            <Select
              value={severity}
              onValueChange={(v) => setSeverity(v as Severity)}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">
                  {t('feedback.modal.severity.low')}
                </SelectItem>
                <SelectItem value="medium">
                  {t('feedback.modal.severity.medium')}
                </SelectItem>
                <SelectItem value="high">
                  {t('feedback.modal.severity.high')}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <ContextSection sentryEventId={prefill.sentryEventId ?? null} />
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={handleClose} disabled={submitting}>
            {t('feedback.modal.cancel')}
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={!valid || submitting}>
            {submitting ? t('feedback.modal.submitting') : t('feedback.modal.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ContextSection({
  sentryEventId,
}: {
  sentryEventId: string | null;
}): JSX.Element {
  const { t } = useTranslation();
  const url = typeof window !== 'undefined' ? window.location.href : '';
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  return (
    <div className="rounded-md border border-dashed border-border bg-muted/40 p-3">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {t('feedback.modal.context.heading')}
      </div>
      <dl className="mt-2 grid grid-cols-1 gap-1 text-xs">
        <Row label={t('feedback.modal.context.url')} value={url} />
        <Row label={t('feedback.modal.context.userAgent')} value={ua} />
        {sentryEventId ? (
          <Row label={t('feedback.modal.context.sentryEventId')} value={sentryEventId} />
        ) : null}
      </dl>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex flex-wrap gap-x-2">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="break-all text-foreground">{value}</dd>
    </div>
  );
}
